/**
 * `/assess/:token` — public page a candidate opens from the invite text.
 * States: loading → notice step(s) → thank-you; `completed` invites go
 * straight to thank-you; an unknown token gets a friendly dead-link page.
 */
import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { errorMessage } from '@/components/Toast';
import { isInvalidInvite, useCompleteInvite, useInvite, type Invite } from './assess-api';
import { AssessLayout, AssessPanel, primaryButtonClass } from './AssessLayout';
import { ASSESSMENT_STEPS, buildResult, isLastStep } from './steps';

function Loading() {
  return (
    <AssessPanel>
      <div className="flex items-center gap-2 text-[15px] text-muted" role="status" aria-busy="true">
        <Loader2 size={18} strokeWidth={2} className="animate-spin text-navy" aria-hidden /> Loading your assessment…
      </div>
    </AssessPanel>
  );
}

function InvalidLink() {
  return (
    <AssessPanel labelledBy="assess-title">
      <h1 id="assess-title" className="text-[22px] font-semibold leading-7">This link isn't valid</h1>
      <p className="mt-3 text-[15px] leading-6 text-gray-700">
        It may have been typed in by hand or belong to someone else. Open the link from the text message we sent you,
        or reply to that text and we'll send a new one.
      </p>
    </AssessPanel>
  );
}

function LoadFailed({ message, retry }: { message: string; retry: () => void }) {
  return (
    <AssessPanel labelledBy="assess-title">
      <h1 id="assess-title" className="text-[22px] font-semibold leading-7">We couldn't load your assessment</h1>
      <p className="mt-3 text-[15px] leading-6 text-gray-700">{message}</p>
      <button type="button" onClick={retry} className={`${primaryButtonClass} mt-5`}>Try again</button>
    </AssessPanel>
  );
}

function ThankYou({ invite }: { invite: Invite }) {
  // The Done button that had focus is gone; land the keyboard/screen-reader user on the
  // new heading instead of letting focus fall back to <body> (QA stage 3).
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  return (
    <AssessPanel labelledBy="assess-title">
      <CheckCircle2 size={32} strokeWidth={2} className="text-success" aria-hidden />
      <h1 id="assess-title" ref={heading} tabIndex={-1} className="mt-3 text-[22px] font-semibold leading-7 outline-none">
        Thanks, {invite.first_name} — you're all set.
      </h1>
      <p className="mt-3 text-[15px] leading-6 text-gray-700">
        We've noted it on your {invite.role_title} application. Someone from the hiring team will be in touch about next
        steps. You can close this page.
      </p>
    </AssessPanel>
  );
}

function Steps({ invite, token }: { invite: Invite; token: string }) {
  const [index, setIndex] = useState(0);
  const complete = useCompleteInvite(token);
  const step = ASSESSMENT_STEPS[index] ?? ASSESSMENT_STEPS[0]!;
  const last = isLastStep(index);
  const onNext = () => (last ? complete.mutate(buildResult()) : setIndex((i) => i + 1));

  return (
    <AssessPanel labelledBy="assess-title">
      <h1 id="assess-title" className="text-[22px] font-semibold leading-7">
        Hi {invite.first_name}, your {invite.role_title} assessment
      </h1>
      {ASSESSMENT_STEPS.length > 1 && (
        <p className="mt-1 text-[13px] text-muted">
          Step {index + 1} of {ASSESSMENT_STEPS.length} · {step.title}
        </p>
      )}
      {step.kind === 'notice' && (
        <div className="mt-4 space-y-3 text-[15px] leading-6 text-gray-700">
          <p>
            Thanks for applying to YES! Automotive. This short work-style assessment helps us understand how you like to
            work with customers and teammates — there are no right or wrong answers.
          </p>
          <p>
            We're putting the finishing touches on the questions. Tap <strong className="font-semibold text-gray-900">Done</strong> so
            we know you opened the link, and we'll text you when it's ready.
          </p>
        </div>
      )}
      {complete.isError && (
        <p role="alert" className="mt-4 rounded-md bg-danger-bg px-3 py-2 text-[13px] text-danger">
          That didn't go through: {errorMessage(complete.error)} Check your connection and tap Done again.
        </p>
      )}
      <button type="button" onClick={onNext} disabled={complete.isPending} className={`${primaryButtonClass} mt-6`}>
        {complete.isPending ? 'Saving…' : last ? 'Done' : 'Next'}
      </button>
    </AssessPanel>
  );
}

export default function AssessPage() {
  const { token } = useParams<{ token: string }>();
  const invite = useInvite(token);

  // The tab title is the only "page name" a phone shows; restore the app's on unmount.
  useEffect(() => {
    const previous = document.title;
    document.title = 'Your assessment — YES! Automotive';
    return () => {
      document.title = previous;
    };
  }, []);

  let body: React.ReactNode;
  if (!token || (invite.isError && isInvalidInvite(invite.error))) body = <InvalidLink />;
  else if (invite.isPending) body = <Loading />;
  else if (invite.isError || !invite.data) body = <LoadFailed message={errorMessage(invite.error)} retry={() => void invite.refetch()} />;
  else if (invite.data.status === 'completed') body = <ThankYou invite={invite.data} />;
  else body = <Steps invite={invite.data} token={token} />;

  return <AssessLayout>{body}</AssessLayout>;
}
